<template>
  <b-container>
    <config-publisher
      ref="configPublisher"
      v-model="config"
      class="p-0"
      tool-name="Sponsorship Configuration Tool"
      tool-icon-text="Money, Money, Money"
      :config-name="configName"
      :can-edit="canSponsorshipConfigEdit"
      :can-publish="canSponsorshipConfigPublish"
      :is-form-valid="isFormValid">
      <template slot="header" />
    </config-publisher>
    <b-form
      v-if="config.scoreboard"
      ref="form">
      <b-card class="mt-2">
        <b-card
          header="Scoreboard"
          header-tag="h4"
          class="mt-3">
          <b-row>
            <b-col>
              <b-form-group>
                <b-form-checkbox
                  v-model="config.scoreboard.disableForPaidUsers">
                  Disable for Paid Users
                </b-form-checkbox>
              </b-form-group>
              <h5>Default Images</h5>
              <image-upload-form
                ref="defaultImageDarkEn"
                v-model="config.scoreboard.defaultImageDarkEn"
                :can-sponsorship-config-edit="canSponsorshipConfigEdit"
                label="Dark Mode English"
                ref-id="defaultImageDarkEn"
                :required="false"
                @on-error="onFormError" />
              <image-upload-form
                v-model="config.scoreboard.defaultImageDarkEs"
                :can-sponsorship-config-edit="canSponsorshipConfigEdit"
                label="Dark Mode Spanish"
                ref-id="defaultImageDarkEs"
                :required="false"
                @on-error="onFormError" />
              <image-upload-form
                v-model="config.scoreboard.defaultImageLightEn"
                :can-sponsorship-config-edit="canSponsorshipConfigEdit"
                label="Light Mode English"
                ref-id="defaultImageLightEn"
                :required="false"
                @on-error="onFormError" />
              <image-upload-form
                v-model="config.scoreboard.defaultImageLightEs"
                :can-sponsorship-config-edit="canSponsorshipConfigEdit"
                label="Light Mode Spanish"
                ref-id="defaultImageLightEs"
                :required="false"
                @on-error="onFormError" />
            </b-col>
          </b-row>
        </b-card>
      </b-card>
    </b-form>
    <b-modal
      id="image-override"
      ref="imageOverrideModal"
      title="Image Overrides"
      hide-footer
      size="lg">
      <b-form
        class="mt-3"
        @submit.prevent="onImageOverrideSave"
        @reset="$refs.imageOverrideModal.hide()">
        <template v-if="selectedDate">
          <image-upload-form
            v-model="selectedDate.imageDarkEn"
            :can-sponsorship-config-edit="canSponsorshipConfigEdit"
            :required="false"
            label="Dark Mode English"
            ref-id="imageDarkEn"
            @on-error="onFormError" />
          <image-upload-form
            v-model="selectedDate.imageDarkEs"
            :can-sponsorship-config-edit="canSponsorshipConfigEdit"
            :required="false"
            label="Dark Mode Spanish"
            ref-id="imageDarkEs"
            @on-error="onFormError" />
          <image-upload-form
            v-model="selectedDate.imageLightEn"
            :can-sponsorship-config-edit="canSponsorshipConfigEdit"
            :required="false"
            label="Light Mode English"
            ref-id="imageLightEn"
            @on-error="onFormError" />
          <image-upload-form
            v-model="selectedDate.imageLightEs"
            :can-sponsorship-config-edit="canSponsorshipConfigEdit"
            :required="false"
            label="Light Mode Spanish"
            ref-id="imageLightEs"
            @on-error="onFormError" />
        </template>
        <b-btn
          type="submit"
          variant="primary"
          class="ml-2 xl-wide float-right">
          Save
        </b-btn>
        <b-btn
          type="reset"
          variant="outline-secondary"
          class="xl-wide float-right">
          Cancel
        </b-btn>
      </b-form>
    </b-modal>
  </b-container>
</template>

<script>
import { mapGetters } from "vuex"
import ConfigPublisher from "@/components/ConfigPublisher"
import ImageUploadForm from "../components/SponsorshipConfig/ImageUploadForm"
import moment from "moment"

export default {
  name: "SponsorshipConfig",
  components: {
    ConfigPublisher,
    ImageUploadForm,
  },
  data() {
    return {
      validated: true,
      config: {},
      recurringDates: [],
      selectedDate: null,
      betaConfig: null,
      prodConfig: null,
      betaUrl: null,
      prodUrl: null,
      configName: "sponsorship",
    }
  },
  computed: {
    ...mapGetters([
      "betaEnvName",
      "liveEnvName",
      "isAdmin",
      "canSponsorshipConfigEdit",
      "canSponsorshipConfigPublish",
    ]),
    calendarAttrs() {
      return this.config.scoreboard.dates.map((d) => {
        return {
          key: d.date,
          highlight: true,
          bar: !!(
            d.imageLightEs ||
            d.imageLightEn ||
            d.imageDarkEs ||
            d.imageDarkEn
          ),
          dates: moment(d.date, "YYYYMMDD").toDate(),
        }
      })
    },
  },
  watch: {
    recurringDates: {
      deep: true,
      handler(data) {
        const formattedDates = data.map((d) => moment(d).format("YYYYMMDD"))
        // remove any dates no longer selected
        this.config.scoreboard.dates = this.config.scoreboard.dates.filter(
          (d) => {
            return formattedDates.includes(d.date)
          }
        )

        // add any missing dates
        formattedDates.forEach((d) => {
          if (!this.config.scoreboard.dates.map((d) => d.date).includes(d)) {
            this.config.scoreboard.dates.push({ date: d })
          }
        })

        // sort array
        this.config.scoreboard.dates = this.config.scoreboard.dates.sort(
          (a, b) => {
            return moment(a.date, "YYYYMMDD") < moment(b.date, "YYYYMMDD")
              ? -1
              : 1
          }
        )
      },
    },
  },
  methods: {
    onDayClick(evt) {
      const dateStr = moment(evt.id).format("YYYYMMDD")
      this.selectedDate = this.config.scoreboard.dates.find(
        (d) => d.date === dateStr
      )
      if (this.selectedDate) {
        this.$refs.imageOverrideModal.show()
      }
    },
    onImageOverrideSave() {
      const idx = this.config.scoreboard.dates.findIndex(
        (d) => d.date === this.selectedDate.date
      )
      if (idx) {
        this.config.scoreboard.dates[idx] = this.selectedDate
      }
      this.$refs.imageOverrideModal.hide()
    },
    onFormError() {
      this.validated = false
    },
    isFormValid() {
      return this.validated
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (this.$refs.configPublisher.configHasChanged()) {
      const message = `You have unpublished changes to your config, which will be lost.\n\nClick "Cancel" to go back and publish your work, or click "Yes" to lose your changes.`
      next(await this.$confirm.open(message))
    } else {
      next()
    }
  },
}
</script>

<style scoped>
</style>
